import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Formik } from "formik";
import { init, t } from "i18next";
import * as Yup from "yup";

import { camelToSnake } from "caseparser";

import { FIELD_REQUIRED_MESSAGE } from "@/shared/utils/validations";

import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { ButtonPrimary } from "@/shared/components/atoms/ButtonPrimary";
import { MultiSelect } from "@/shared/components/organisms/MultiSelect";
import { Select } from "@/shared/components/organisms/Select";
import { toasts } from "@/shared/utils/toasts";

import { selectBranch } from "@/app/branchs/branch.slice";
import { selectUser } from "@/app/auth/auth.slice";

// API
import {
  useGeneralConfigMutation,
  useSetConfigMutation,
  useGetConfigQuery,
} from "@/app/siigo/siigo.api";

// Esquema de validación con Yup

// code: Yup.string().required(FIELD_REQUIRED_MESSAGE),
// user: Yup.string().required(FIELD_REQUIRED_MESSAGE),
// password: Yup.string().required(FIELD_REQUIRED_MESSAGE),
// terminal: Yup.string().required(FIELD_REQUIRED_MESSAGE),

const validationSchema = Yup.object().shape({
  seller_id: Yup.number().required(FIELD_REQUIRED_MESSAGE),
  document_type_sale: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  document_type_expense: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  payment_types: Yup.array()
    .of(Yup.number().required(FIELD_REQUIRED_MESSAGE))
    .required(FIELD_REQUIRED_MESSAGE),
  taxes: Yup.array()
    .of(Yup.number().required(FIELD_REQUIRED_MESSAGE))
    .required(FIELD_REQUIRED_MESSAGE),
});

export const ElectronicFinance: FC<any> = () => {
  const { id } = useParams();
  const user = useSelector(selectUser);
  const branch =  useSelector(selectBranch);

  // Verificar si branch está definido
  if (!branch) {
    return <div>Loading...</div>;
  }

  const [initialValues, setInitialValues] = useState({
    code: branch.gti_code || "",
    user: branch.gti_user || "",
    password: branch.gti_password || "",
    terminal: branch.gti_terminal || "",
    seller_id: "",
    document_type_sale: "",
    document_type_expense: "",
    payment_types: [],
    taxes: [],
  }); 

  const getConfigSiigo = useGetConfigQuery({
    branchId: id,
  });

  useEffect(() => {
    if (getConfigSiigo.isSuccess) {
      setInitialValues({
        ...initialValues,
        seller_id: getConfigSiigo.data.sellerId,
        document_type_sale: getConfigSiigo.data.documentTypeSale,
        document_type_expense: getConfigSiigo.data.documentTypeExpense,
        payment_types: getConfigSiigo.data.paymentTypes,
        taxes: getConfigSiigo.data.taxes,
      });
    } else if (getConfigSiigo.isError) {
      toasts.info(
        "Información",
        "Esta filial no tiene configurada la integración con SIIGO"
      );
    }
  }, [getConfigSiigo]);

  const [setConfigSiigo, setConfigSiigoResult] = useSetConfigMutation();

  useEffect(() => {
    if (setConfigSiigoResult.isSuccess) {
      toasts.success("Configuración", "Configuración SIIGO guardada correctamente");
    } else if (setConfigSiigoResult.isError) {
      toasts.danger(
        "Error",
        "Error al guardar la configuración, por favor verifica los datos"
      );
    }
  }, [setConfigSiigoResult]);

  const [generalConfig, generalConfigResult] = useGeneralConfigMutation();

  useEffect(() => {
    if (branch.id) {
      generalConfig({
        branchId: branch.id,
      });
    }
  }, [branch]);

  const [optionsDocumentTypeExpense, setOptionsDocumentTypeExpense] = useState([]);
  const [optionsDocumentTypeSale, setOptionsDocumentTypeSale] = useState([]);
  const [optionsPaymentTypesExpense, setOptionsPaymentTypesExpense] = useState([]);
  const [optionsPaymentTypesSale, setOptionsPaymentTypesSale] = useState([]);
  const [optionsTaxes, setOptionsTaxes] = useState([]);
  const [optionsSellers, setOptionsSellers] = useState([]);

  useEffect(() => {
    if (generalConfigResult.isSuccess) {
      const documentTypesExpense = generalConfigResult.data.documentTypesExpense.map((documentType: any) => ({
        id: documentType.id,
        name: `${documentType.name} (${documentType.description})`,
      }));
      setOptionsDocumentTypeExpense(documentTypesExpense);

      const documentTypesSale = generalConfigResult.data.documentTypesSale.map((documentType: any) => ({
        id: documentType.id,
        name: `${documentType.name} (${documentType.description})`,
      }));
      setOptionsDocumentTypeSale(documentTypesSale);

      const paymentTypesExpense = generalConfigResult.data.paymentTypesExpense.map((paymentType: any) => ({
        id: paymentType.id,
        name: paymentType.name,
      }));
      setOptionsPaymentTypesExpense(paymentTypesExpense);

      const paymentTypesSale = generalConfigResult.data.paymentTypesSale.map((paymentType: any) => ({
        id: paymentType.id,
        name: paymentType.name,
      }));
      setOptionsPaymentTypesSale(paymentTypesSale);

      const taxes = generalConfigResult.data.taxes.map((tax: any) => ({
        id: tax.id,
        name: tax.name,
      }));
      setOptionsTaxes(taxes);

      const sellers = generalConfigResult.data.users.map((seller: any) => ({
        id: seller.id,
        name: `${seller.first_name} ${seller.last_name}`,
      }));
      setOptionsSellers(sellers);
    } else if (generalConfigResult.isError) {
      toasts.danger(
        "Error",
        "Acceso no permitido"
      );
    }
  }, [generalConfigResult]);

  return (
    <div className="flex flex-col items-center h-full w-full mt-2">
      <div className="w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
        <div className="flex sm:flex sm:items-start px-5 mt-6">
          <div className="flex-grow">
            <h1 className="text-lg font-semibold leading-6 text-gray-900">
              {branch.gti_user !== "null" ? "GTI" : "SIIGO"}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Configura las llaves de {branch.gti_user !== "null"? "GTI" : "SIIGO"}{" "}
              para habilitar la facturación.
            </p>
          </div>
        </div>

        <div className="border-b border-gray-300 my-3"></div>

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, formikHelpers) => {
            console.log("VALORES DE REGISTRO", values);
            console.log("Usuario", user);

            if (branch.gti_user !== "null") {
              
            }else{
              setConfigSiigo({
                branchId: id,
                ...values,
              });
            }

            //formikHelpers.resetForm();
          }}
        >
          {({
            handleChange,
            handleSubmit,
            errors,
            values,
            setFieldValue,
          }) => (
            <form
              onSubmit={() => {
                handleSubmit();
                console.log(errors);
              }}
              className="w-full flex flex-col items-center justify-center"
            >
              <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
                {/* Campos para GTI 
                {(branch.gateway === "stripe" || branch.gateway == "wompi") && (
                  <>
                    <div className="col-span-1">
                      <InputWithCornerHint
                        label={t("gti.views.code")}
                        span={t("general.mandatory")}
                        id="code"
                        name="code"
                        type="text"
                        placeholder={t("gti.views.code")}
                        onChange={handleChange}
                        value={values.code}
                        error={!!errors.code}
                      />
                    </div>
                    <div className="col-span-1">
                      <InputWithCornerHint
                        label={t("gti.views.user")}
                        span={t("general.mandatory")}
                        id="user"
                        name="user"
                        type="text"
                        placeholder={t("gti.views.user")}
                        onChange={handleChange}
                        value={values.user}
                        error={!!errors.user}
                      />
                    </div>
                    <div className="col-span-1">
                      <InputWithCornerHint
                        label={t("gti.views.password")}
                        span={t("general.mandatory")}
                        id="password"
                        name="password"
                        type="text"
                        placeholder={t("gti.views.password")}
                        onChange={handleChange}
                        value={values.password}
                        error={!!errors.password}
                      />
                    </div>
                    <div className="col-span-1">
                      <InputWithCornerHint
                        label={t("branch.views.terminal")}
                        span={t("general.mandatory")}
                        id="terminal"
                        name="terminal"
                        type="text"
                        placeholder={t("branch.views.terminal")}
                        onChange={handleChange}
                        value={values.terminal}
                        error={!!errors.terminal}
                      />
                    </div>
                  </>
                )}
                */}

                {/* Campos adicionales para Siigo */}
                {branch.gti_user == "null" && (
                  <>
                    <div className="col-span-1">
                      <Select
                        name="seller_id"
                        span={t("general.mandatory")}
                        selected={
                          optionsSellers
                            ? optionsSellers.find(
                                (option) => option.id == values.seller_id
                              )
                            : null
                        }
                        setSelected={(value) =>
                          setFieldValue("seller_id", value.id)
                        }
                        options={optionsSellers}
                        tittle={t("siigo.views.seller_id")}
                        error={!!errors.seller_id}
                        textError={errors.seller_id}
                      />
                    </div>
                    <div className="col-span-1">
                      <Select
                        name="document_type_sale"
                        span={t("general.mandatory")}
                        selected={
                          optionsDocumentTypeSale
                            ? optionsDocumentTypeSale.find(
                                (option) => option.id == values.document_type_sale
                              )
                            : null
                        }
                        setSelected={(value) =>
                          setFieldValue("document_type_sale", value.id)
                        }
                        options={optionsDocumentTypeSale}
                        tittle={t("siigo.views.document_type_sale")}
                        error={!!errors.document_type_sale}
                        textError={errors.document_type_sale}
                      />
                    </div>
                    <div className="col-span-1">
                      <Select
                        name="document_type_expense"
                        span={t("general.mandatory")}
                        selected={
                          optionsDocumentTypeExpense
                            ? optionsDocumentTypeExpense.find(
                                (option) => option.id == values.document_type_expense
                              )
                            : null
                        }
                        setSelected={(value) =>
                          setFieldValue("document_type_expense", value.id)
                        }
                        options={optionsDocumentTypeExpense}
                        tittle={t("siigo.views.document_type_expense")}
                        error={!!errors.document_type_expense}
                        textError={errors.document_type_expense}
                      />
                    </div>
                    <div className="col-span-1">
                      <MultiSelect
                        title={t("siigo.views.payment_types")}
                        span={t("general.mandatory")}
                        selectedOptions={optionsPaymentTypesSale.filter((fee) =>
                          values.payment_types.includes(fee.id)
                        )}
                        setSelectedOptions={(selected) =>
                          setFieldValue(
                            "payment_types",
                            selected.map((fee) => fee.id) // Mantén los valores como un array
                          )
                        }
                        options={optionsPaymentTypesSale}
                      />
                    </div>
                    <div className="col-span-1">
                      <MultiSelect
                        title={t("siigo.views.taxes")}
                        span={t("general.mandatory")}
                        selectedOptions={optionsTaxes.filter((fee) =>
                          values.taxes.includes(fee.id)
                        )}
                        setSelectedOptions={(selected) =>
                          setFieldValue(
                            "taxes",
                            selected.map((fee) => fee.id) // Mantén los valores como un array
                          )
                        }
                        options={optionsTaxes}
                      />
                    </div>
                  </>
                )}

                {/* Botón "Guardar" centrado */}
                <div className="col-span-4 mt-5 flex justify-center">
                  <ButtonPrimary
                    onClick={() => {
                      handleSubmit();
                      console.log(errors);
                    }}
                    disabled={setConfigSiigoResult.isLoading}
                    className="mt-5"
                    text="Guardar"
                    size="large"
                    type="button"
                  />
                </div>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};